import React, { useEffect, useMemo, useRef, useState } from "react";

import { faCube, faEdit } from "@fortawesome/pro-regular-svg-icons";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { keyBy } from "lodash-es";
import { FormProvider, useForm } from "react-hook-form";

import { Flow, FlowVersion, FlowVersionFlowChild } from "src/api/flowTypes";
import { useFlow } from "src/api/queries";
import { AutomapButton } from "src/auto-mapping/AutomapButton";
import { Button } from "src/base-components/Button";
import { ExternalLink } from "src/base-components/ExternalLink";
import { LoadingView } from "src/base-components/LoadingView";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { useRevision } from "src/changeHistory/queries";
import { AutoMappingResponse } from "src/clients/copilot";
import { FlowNode, FlowNodeData } from "src/constants/NodeDataTypes";
import { useSchemas } from "src/datasets/utils";
import { Accordion } from "src/design-system/Accordion";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { DataMap } from "src/parentFlowNodes/DataMap";
import { ChangeVersionModal } from "src/parentFlowNodes/flowNode/ChangeVersionModal";
import { SelectedVersion } from "src/parentFlowNodes/flowNode/SelectedVersion";
import { FlowNodeForm } from "src/parentFlowNodes/flowNode/types";
import { useGetChildVersionLink } from "src/parentFlowNodes/useGetChildVersionLink";
import { useHighlightFields } from "src/parentFlowNodes/useHighlightFields";
import { useUpdateKnownEtag } from "src/parentFlowNodes/useUpdateKnownEtag";
import {
  queryErrorLikelyBecauseChildFlowGotDeleted,
  setupFlowNodeForm,
} from "src/parentFlowNodes/utils";
import { ErrorContent } from "src/router/ErrorPage";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  selectedNode: FlowNode;
  resultsOutdated?: boolean;
  lazyInputs: boolean;
} & NodeEditorBaseProps<FlowNodeData>;

export type FlowNodeCurrentChildVersion = FlowVersionFlowChild;

type LoadedViewProps = {
  childFlow: Flow;
  childVersionCurrent?: FlowNodeCurrentChildVersion;
  childVersionRevision?: Pick<
    FlowVersion,
    "etag" | "input_schema" | "output_schema"
  >;
  nodeData: FlowNodeData;
  nodeId: string;
  isReactive: boolean;
  isImmutable: boolean;
  resultsOutdated: boolean;
  onUpdate: Props["onUpdate"];
  lazyInputs: boolean;
};

const LoadedView: React.FC<LoadedViewProps> = ({
  childFlow,
  childVersionCurrent,
  childVersionRevision,
  nodeData,
  nodeId,
  isImmutable,
  isReactive,
  resultsOutdated,
  onUpdate,
  lazyInputs,
}) => {
  const { orgId, workspace, version } = useAuthoringContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const childUiSchemas = useSchemas(childVersionRevision);
  const handleSubmitRef = useRef<VoidFunction>(() => {});
  const { highlightedFields, highlightInputFields, highlightOutputFields } =
    useHighlightFields();
  const isReactiveRef = useRef(isReactive);

  const getChildFlowLink = useGetChildVersionLink();

  const [defaultValues, schemaMappingErrors, fieldsToRemove] = useMemo(() => {
    if (isImmutable) {
      return [
        {
          input_mappings: nodeData.input_mappings,
          output_mappings: nodeData.output_mappings,
          child_flow_version_id: nodeData.child_flow_version_id,
          child_flow_version_known_etag: nodeData.child_flow_version_known_etag,
        },
        [],
        [],
      ];
    } else {
      return setupFlowNodeForm(nodeData, childUiSchemas);
    }
  }, [isImmutable, nodeData, childUiSchemas]);

  const formMethods = useForm<FlowNodeForm>({
    defaultValues,
    resetOptions: { keepTouched: true },
    ...(isReactive && { values: defaultValues }),
  });

  const { unregister } = formMethods;

  const handleAutoMapping = (response: AutoMappingResponse) => {
    const currentInputMappings = formMethods.getValues("input_mappings");
    const currentOutputMappings = formMethods.getValues("output_mappings");

    const updatedInputMappings = { ...currentInputMappings };
    const updatedOutputMappings = { ...currentOutputMappings };

    const [inputFields, outputFields] = [[] as string[], [] as string[]];

    response.mappings.forEach((mapping) => {
      const inputFieldId = Object.keys(currentInputMappings).find(
        (id) => currentInputMappings[id].destination === mapping.destination,
      );

      if (inputFieldId) {
        updatedInputMappings[inputFieldId] = {
          ...updatedInputMappings[inputFieldId],
          source: mapping.source,
        };
        inputFields.push(mapping.destination);
      }

      const outputFieldId = Object.keys(currentOutputMappings).find(
        (id) => currentOutputMappings[id].source === mapping.source,
      );

      if (outputFieldId) {
        updatedOutputMappings[outputFieldId] = {
          ...updatedOutputMappings[outputFieldId],
          destination: mapping.destination.replace(/^data\./, ""),
        };
        outputFields.push(mapping.source);
      }
    });

    if (!isReactiveRef.current) {
      // Update the form with the new mappings
      formMethods.setValue("input_mappings", updatedInputMappings, {
        shouldTouch: true,
      });
      formMethods.setValue("output_mappings", updatedOutputMappings, {
        shouldTouch: true,
      });
      highlightInputFields(inputFields);
      highlightOutputFields(outputFields);

      tracker.emit(
        trackingEvents.flowNodeApplyAutomap({
          flow_version_id: version.id,
          node_id: nodeId,
          etag: version.etag ?? "",
          is_loop_node: false,
          automapping_id: response.id,
          organization_id: workspace.organization_id,
        }),
      );
    } else {
      logger.warn("Unable to apply auto-mapping in reactive mode", response);
    }
  };

  // keep refs up to date
  handleSubmitRef.current = formMethods.handleSubmit((data) => {
    onUpdate({
      nodeId,
      newData: {
        ...nodeData,
        ...data,
      },
    });
  });

  useSubmitForm({
    onChange: (data: FlowNodeForm) => {
      onUpdate({ newData: { ...nodeData, ...data } });
    },
    disabled: isReactive || isImmutable,
    previousValues: defaultValues,
    watch: formMethods.watch,
  });

  useEffect(() => {
    // Prevent unnecessary submits because they e.g. result in change history entries
    if (fieldsToRemove.length > 0) {
      unregister(fieldsToRemove);
      if (!isReactive && !isImmutable) {
        handleSubmitRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unregister, fieldsToRemove.join(","), isReactive, isImmutable]);

  useEffect(() => {
    isReactiveRef.current = isReactive;
  }, [isReactive]);

  const { renderedInDiffView } = useDiffViewContext();

  useUpdateKnownEtag({
    nodeId: nodeId,
    nodeData,
    immutable: isImmutable,
    isReactive,
    onUpdate,
    etag: childVersionCurrent?.etag,
  });

  if (!childVersionRevision || !childVersionCurrent) {
    return <>Unable to load child version</>;
  }

  const childFlowName = `${childFlow.name}, ${childVersionCurrent.name}`;

  const inputMappingErrors = schemaMappingErrors.filter(
    (e) => e.type === "input",
  );
  const outputMappingErrors = schemaMappingErrors.filter(
    (e) => e.type === "output",
  );
  const fieldMappingErrors = schemaMappingErrors.filter(
    (e) => e.issue === "field-missing",
  );
  const isStale = !!nodeData.is_stale;
  const hasMissingSchema = !childUiSchemas.input || !childUiSchemas.output;

  return (
    <FormProvider {...formMethods}>
      <div className="flex flex-col gap-y-4">
        {hasMissingSchema && isStale && (
          <Callout type="warning">
            To complete the configuration, inputs and outputs need to be mapped.
          </Callout>
        )}
        <Accordion defaultValue={["version", "mapping"]}>
          <Accordion.Item
            isReadOnly={renderedInDiffView}
            rightSlot={
              <Tooltip placement="bottom-end" title="Open in new tab" asChild>
                <div>
                  <ExternalLink
                    href={getChildFlowLink({
                      orgId,
                      workspaceId: workspace.id,
                      flowId: childFlow.id,
                      versionId: childVersionCurrent.id,
                    })}
                  >
                    <Icon icon={faUpRightFromSquare} size="xs" />
                  </ExternalLink>
                </div>
              </Tooltip>
            }
            label="Select the Decision Flow you want to call"
            value="version"
          >
            <div className="flex flex-col items-start">
              <SelectedVersion
                flowName={childFlow.name}
                version={childVersionCurrent}
              />
              <Button
                dataLoc="change-child-version-button"
                disabled={isImmutable}
                iconLeft={faEdit}
                size="sm"
                variant="secondary"
                onClick={() => setIsModalOpen(true)}
              >
                Change version
              </Button>
            </div>
          </Accordion.Item>
          <Accordion.Item
            isReadOnly={renderedInDiffView}
            rightSlot={
              <AutomapButton
                disabled={isImmutable}
                nodeId={nodeId}
                tooltipTitle={
                  resultsOutdated
                    ? "Auto-mapping uses information from the flow schemas, as well as typing information from test runs. Initiate a test run for the latest typing information"
                    : ""
                }
                type="flow"
                onAutoMapping={handleAutoMapping}
              />
            }
            label="Map inputs and outputs of Decision Flows"
            value="mapping"
          >
            <div className="flex flex-col gap-y-4">
              <DataMap
                childFlowId={childFlow.id}
                childFlowName={childFlowName}
                childFlowSlug={childFlow.slug}
                childVersionId={childVersionCurrent.id}
                description={
                  <>
                    Map the data from this Decision Flow to the inputs of the
                    Child Flow{" "}
                    <span className="text-gray-800">{childFlowName} </span>
                  </>
                }
                fieldMappingErrors={keyBy(
                  inputMappingErrors.filter(
                    (error) => error.issue === "field-missing",
                  ),
                  "fieldName",
                )}
                hasError={fieldMappingErrors.length > 0}
                highlightedFields={highlightedFields.input}
                isNodeStale={isStale}
                lazyInputs={lazyInputs}
                readonly={isImmutable}
                schema={childUiSchemas.input}
                title="Inputs"
              />
              <DataMap
                childFlowId={childFlow.id}
                childFlowName={childFlowName}
                childFlowSlug={childFlow.slug}
                childVersionId={childVersionCurrent.id}
                description={
                  <>
                    Map the outputs from the Child Flow{" "}
                    <span className="text-gray-800">{childFlowName} </span> to
                    the data from this Decision Flow
                  </>
                }
                fieldMappingErrors={keyBy(
                  outputMappingErrors.filter(
                    (error) => error.issue === "field-missing",
                  ),
                  "fieldName",
                )}
                highlightedFields={highlightedFields.output}
                isNodeStale={isStale}
                lazyInputs={lazyInputs}
                readonly={isImmutable}
                schema={childUiSchemas.output}
                title="Outputs"
                isOutput
              />
            </div>
          </Accordion.Item>
        </Accordion>
        <ChangeVersionModal
          flowId={childFlow.id}
          open={isModalOpen}
          selectedVersionId={childVersionCurrent.id}
          versions={childFlow.versions}
          onClose={() => setIsModalOpen(false)}
        />
      </div>
    </FormProvider>
  );
};

export const FlowNodeEditor: React.FC<Props> = ({
  selectedNode,
  lazyInputs,
  onUpdate,
  immutable,
  isReactive,
  resultsOutdated = false,
}) => {
  const childFlowQuery = useFlow(selectedNode.data.child_flow_id ?? "", {
    refetchInterval: 5000,
  });
  const childVersionRevisionQuery = useRevision({
    versionId: selectedNode.data.child_flow_version_id ?? undefined,
    etag: selectedNode.data.child_flow_version_known_etag ?? undefined,
    keepPreviousData: true,
  });

  useEffect(() => {
    toastActions.dismiss(selectedNode.id);
  }, [selectedNode.id]);

  if (selectedNode.data.child_flow_id === null)
    return (
      <div className="flex h-full w-full items-center justify-center rounded-lg border border-gray-200">
        <EmptyState
          description="No version has been selected for the called Decision Flow."
          headline="Version does not exist"
          icon={faCube}
        />
      </div>
    );
  return (
    <>
      {immutable ? (
        <LoadingView
          queryResult={[childFlowQuery, childVersionRevisionQuery]}
          renderErrored={(error) => {
            if (queryErrorLikelyBecauseChildFlowGotDeleted(error))
              return (
                <div className="flex h-full w-full items-center justify-center rounded-lg border border-gray-200">
                  <EmptyState
                    description="Called Decision Flow version has been deleted."
                    headline="Version does not exist"
                    icon={faCube}
                  />
                </div>
              );
            else return <ErrorContent message={error} />;
          }}
          renderUpdated={([childFlow, childVersionRevision]: [
            Flow,
            FlowVersion,
          ]) => {
            const childVersionUpToDate = childFlow.versions.find(
              (version) =>
                version.id === selectedNode.data.child_flow_version_id,
            );
            return (
              <LoadedView
                childFlow={childFlow}
                childVersionCurrent={childVersionUpToDate}
                childVersionRevision={childVersionRevision}
                isImmutable={immutable}
                isReactive={isReactive}
                lazyInputs={lazyInputs}
                nodeData={selectedNode.data}
                nodeId={selectedNode.id}
                resultsOutdated={resultsOutdated}
                onUpdate={onUpdate}
              />
            );
          }}
        />
      ) : (
        <LoadingView
          queryResult={childFlowQuery}
          renderUpdated={(childFlow: Flow) => {
            const childVersionUpToDate = childFlow.versions.find(
              (version) =>
                version.id === selectedNode.data.child_flow_version_id,
            );
            return (
              <LoadedView
                childFlow={childFlow}
                childVersionCurrent={childVersionUpToDate}
                childVersionRevision={childVersionUpToDate}
                isImmutable={immutable}
                isReactive={isReactive}
                lazyInputs={lazyInputs}
                nodeData={selectedNode.data}
                nodeId={selectedNode.id}
                resultsOutdated={resultsOutdated}
                onUpdate={onUpdate}
              />
            );
          }}
        />
      )}
    </>
  );
};
